import { useCallback, useReducer, useMemo } from 'react';
import { useTruthyCount } from './useTruthyCount';

type FieldValue = string;
type ValueState<K extends string> = Record<K, FieldValue>;

type ErrorValue = string | null;
type ErrorState<K extends string> = Record<K, ErrorValue>;

interface FormState<K extends string> {
    labels: Record<K, string>;
    values: ValueState<K>;
    errors: ErrorState<K>;
}

type FormAction<K extends string> =
    | {
          type: 'updateField';
          key: K;
          value: FieldValue;
          error: ErrorValue;
      }
    | {
          type: 'setErrors';
          errors: ErrorState<K>;
      }
    | {
          type: 'reset';
          initialValues: ValueState<K>;
          initialErrors: ErrorState<K>;
      };

const createReducer = <K extends string>() => {
    return (state: FormState<K>, action: FormAction<K>): FormState<K> => {
        switch (action.type) {
            case 'updateField':
                return {
                    ...state,
                    values: { ...state.values, [action.key]: action.value },
                    errors: { ...state.errors, [action.key]: action.error },
                };
            case 'setErrors':
                return {
                    ...state,
                    errors: action.errors,
                };
            case 'reset':
                return {
                    ...state,
                    values: action.initialValues,
                    errors: action.initialErrors,
                };
            default:
                return state;
        }
    };
};

export type ValidatorFunction = (value: FieldValue) => ErrorValue;
export type ValueTransformerFunction = (rawValue: FieldValue) => FieldValue;

export type FieldType = 'input' | 'textarea';

export interface FieldDefinition<K extends string = string> {
    key: K;
    label: string;
    validator?: ValidatorFunction;
    type?: FieldType;
    required?: boolean;
    valueTransformer?: ValueTransformerFunction;
}

export type FieldMetadata<K extends string> = Readonly<
    Omit<FieldDefinition<K>, 'type'>
> & {
    readonly type: FieldType;
};

const useFormState = <K extends string>(
    fieldDefinitions: readonly FieldDefinition<K>[]
) => {
    const fields = useMemo(
        () => fieldDefinitions.map((f) => f.key),
        [fieldDefinitions]
    );

    const metadata = useMemo(
        () =>
            fieldDefinitions.reduce(
                (acc, field) => {
                    acc[field.key] = { ...field, type: field.type ?? 'input' };
                    return acc;
                },
                {} as Record<K, FieldMetadata<K>>
            ),
        [fieldDefinitions]
    );

    const initialLabels = useMemo(
        () =>
            fieldDefinitions.reduce(
                (acc, field) => {
                    acc[field.key] = field.label;
                    return acc;
                },
                {} as Record<K, string>
            ),
        [fieldDefinitions]
    );

    const initialState: FormState<K> = useMemo(() => {
        const createEmptyRecord = <V>(defaultValue: V): Record<K, V> =>
            fields.reduce(
                (acc, key) => {
                    acc[key] = defaultValue;
                    return acc;
                },
                {} as Record<K, V>
            );

        return {
            labels: initialLabels,
            values: createEmptyRecord(''),
            errors: createEmptyRecord(null as ErrorValue),
        };
    }, [fields, initialLabels]);

    const reducer = useMemo(() => createReducer<K>(), []);
    const [state, dispatch] = useReducer(reducer, initialState);

    const updateField = useCallback(
        (key: K, value: FieldValue) => {
            const { validator, valueTransformer } = metadata[key];

            const fieldValue = valueTransformer
                ? valueTransformer(value)
                : value;

            const error = validator ? validator(fieldValue) : null;

            dispatch({ type: 'updateField', key, value: fieldValue, error });
        },
        [metadata]
    );

    const validateAll = useCallback(() => {
        const newErrors = fields.reduce(
            (acc, key) => {
                const validator = metadata[key].validator;
                const value = state.values[key];

                acc[key] = validator ? validator(value) : null;

                return acc;
            },
            {} as Record<K, ErrorValue>
        );

        dispatch({ type: 'setErrors', errors: newErrors });

        return Object.values(newErrors).every((error) => error === null);
    }, [fields, metadata, state.values]);

    const reset = useCallback(
        (initialValues?: ValueState<K>, initialErrors?: ErrorState<K>) => {
            dispatch({
                type: 'reset',
                initialValues: initialValues ?? initialState.values,
                initialErrors: initialErrors ?? initialState.errors,
            });
        },
        [initialState.values, initialState.errors]
    );

    const isValid = useTruthyCount(Object.values(state.errors)) === 0;

    return {
        fields: fields as readonly K[],
        labels: state.labels,
        values: state.values,
        errors: state.errors,
        metadata,
        updateField,
        validateAll,
        reset,
        isValid,
    };
};

export type UseFormStateValue<K extends string> = ReturnType<
    typeof useFormState<K>
>;

export default useFormState;
